import { uniqueId } from 'lodash';

import type {
  CombinedStepForTree,
  LinkedWorkflowStep,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { VersionDiffLinkedWorkflowStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/types';
import { createMockNullAction } from 'dashboard/lib/stubs/workflow-step';
import { EMPTY_LOGIC_BLOCK_OR_V2 } from 'dashboard/lib/workflows';
import { BaseTrigger } from 'dashboard/models/trigger';
import {
  WorkflowStep,
  WorkflowStepAction,
  WorkflowStepActionKey,
  WorkflowStepConditional,
  WorkflowStepParallel,
  WorkflowStepPlaceholder,
  WorkflowStepType,
  WorkflowStepWait,
} from 'dashboard/models/workflow-version';
import { generateWorkflowToken } from 'lib/workflows';

export const createMockWaitStep = (token?: string): WorkflowStepWait => {
  return {
    type: WorkflowStepType.Wait,
    token: token || generateWorkflowToken(),
    name: 'wait step',
    'watched-events': [],
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    'wait-period-seconds': null,
  };
};

type MockInquiryActionStepType = 'default' | 'route' | 'placeholder';

export const createMockActionStep = (
  type: MockInquiryActionStepType = 'default',
  nextStepName?: string,
  token?: string,
): WorkflowStepAction => {
  switch (type) {
    case 'default':
      return {
        type: WorkflowStepType.Action,
        token: token || generateWorkflowToken(),
        name: 'default action step',
        action: {
          params: {
            account: '',
            'account-status-id': '1',
          },
          key: 'update_account_status',
          version: 1,
        },
      };
    case 'route':
      return {
        type: WorkflowStepType.Action,
        token: token || generateWorkflowToken(),
        name: 'Update next step',
        action: {
          params: {
            inquiry: BaseTrigger,
            'next-step-name': nextStepName || uniqueId('next_'),
          },
          key: WorkflowStepActionKey.UpdateInquiryNextStepName,
          version: 1,
        },
      };
    case 'placeholder':
      return createMockNullAction({
        name: 'Placeholder Action',
        token: token || generateWorkflowToken(),
      });
  }
};

export const createMockConditionalStep = (
  token?: string,
  branches?: WorkflowStep[][],
): WorkflowStepConditional => {
  branches = branches || [[createMockActionStep('route')], [createMockActionStep('route')]];
  return {
    type: WorkflowStepType.Conditional,
    // follows token pattern for newWorkflowConditional() in dashboard/models/workflow-version
    token: token || `wfc-${generateWorkflowToken()}`,
    name: 'Conditional Step',
    branches: branches,
    conditionals: branches.map((_branch, index) => {
      if (index === branches.length - 1) {
        return { token: `wfc-${generateWorkflowToken()}`, name: 'Else', rules: true };
      } else {
        return {
          token: `wfc-${generateWorkflowToken()}`,
          name: `Route ${index + 1}`,
          rules: EMPTY_LOGIC_BLOCK_OR_V2,
        };
      }
    }),
  };
};

export const createMockParallelStep = (
  token?: string,
  branches?: WorkflowStep[][],
): WorkflowStepParallel => {
  return {
    type: WorkflowStepType.Parallel,
    token: token || generateWorkflowToken(),
    name: 'parallel step',
    branches: branches || [[createMockActionStep()], [createMockActionStep()]],
  };
};

export const createMockPlaceholderWorkflowStep = (token?: string): WorkflowStepPlaceholder => {
  return {
    type: WorkflowStepType.Placeholder,
    // follows token pattern for newWorkflowConditional() in dashboard/models/workflowversion
    token: token || generateWorkflowToken(),
    name: 'Placeholder Step',
  };
};

// TODO (eric): refactor to used named params
export const createMockWorkflowTreeStep = (
  token?: string,
  nextSteps?: string[],
  workflowStep?: WorkflowStep,
  workflowVersionId?: string,
  branches?: CombinedStepForTree[][],
): LinkedWorkflowStep & {
  workflowStepType?: WorkflowStep['type'];
  branches?: CombinedStepForTree[][];
} => {
  workflowStep = workflowStep || createMockActionStep();

  return {
    type: 'workflow',
    token: token || generateWorkflowToken(),
    workflowVersionId: workflowVersionId || 'wfv_123',
    nextSteps: nextSteps || [],
    workflowStep: workflowStep,
    workflowStepType: workflowStep.type,
    branches: branches || [],
  };
};

export const createMockVersionDiffLinkedWorkflowStep = (
  token?: string,
  nextSteps?: string[],
  workflowStep?: WorkflowStep,
  workflowVersionId?: string,
  updateInquiryNextStepActionStepToken?: string,
): VersionDiffLinkedWorkflowStep => {
  workflowStep = workflowStep || createMockActionStep();

  return {
    type: 'workflow',
    token: token || generateWorkflowToken(),
    workflowVersionId: workflowVersionId || 'wfv_123',
    nextSteps: nextSteps || [],
    workflowStep: workflowStep,
    updateInquiryNextStepActionStepToken: updateInquiryNextStepActionStepToken || '',
  };
};
